import { useEffect, useState } from "react";
import { communauteApi } from "@/lib/api";
import { toast } from "sonner";
import {
  PageHeader,
  Kpi,
  Badge,
  Btn,
  EmptyState,
  FieldLabel,
  Select,
} from "@/components/DashboardUI";
import { Images, Check, X, Trash2 } from "lucide-react";

export default function Communaute() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, enAttente: 0, approuvees: 0 });
  const [filtreStatut, setFiltreStatut] = useState("");

  const reload = async () => {
    try {
      const params = filtreStatut ? { statut: filtreStatut } : {};
      const [list, s] = await Promise.all([
        communauteApi.list(params),
        communauteApi.stats(),
      ]);
      setItems(list);
      setStats(s);
    } catch {
      toast.error("Erreur");
    }
  };
  useEffect(() => {
    reload();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filtreStatut]);

  async function moderer(id, statut, publieGalerie) {
    try {
      await communauteApi.moderer(id, { statut, publieGalerie });
      toast.success("Modéré");
      reload();
    } catch {
      toast.error("Erreur");
    }
  }

  async function supprimer(id) {
    if (!window.confirm("Supprimer ?")) return;
    try {
      await communauteApi.remove(id);
      toast.success("Supprimé");
      reload();
    } catch {
      toast.error("Erreur");
    }
  }

  const tone = (s) =>
    s === "APPROUVEE" ? "success" : s === "REJETEE" ? "danger" : "neutral";

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="communaute-page">
      <PageHeader
        chapter="Chapitre pro · Le grimoire"
        title="Communauté"
        subtitle="Modération des photos et souvenirs partagés."
        testId="comm-header"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi value={stats.total} label="Total" accent="parchemin" testId="comm-total" />
        <Kpi
          value={stats.enAttente}
          label="En attente"
          accent="or"
          testId="comm-attente"
        />
        <Kpi
          value={stats.approuvees}
          label="Approuvées"
          accent="vert"
          testId="comm-appr"
        />
        <div className="border border-or/15 bg-noir-50/50 rounded-sm p-4 flex flex-col gap-2">
          <FieldLabel>Filtre statut</FieldLabel>
          <Select
            data-testid="filtre-statut"
            value={filtreStatut}
            onChange={(e) => setFiltreStatut(e.target.value)}
          >
            <option value="" className="bg-noir">
              Tous
            </option>
            <option value="EN_ATTENTE" className="bg-noir">
              En attente
            </option>
            <option value="APPROUVEE" className="bg-noir">
              Approuvées
            </option>
            <option value="REJETEE" className="bg-noir">
              Rejetées
            </option>
          </Select>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<Images className="w-6 h-6" />}
          title="Aucune photo communauté."
          testId="comm-empty"
        />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((photo) => (
            <div
              key={photo.id}
              className="bg-noir-50/50 border border-or/15 hover:border-or/40 rounded-sm overflow-hidden transition-all duration-300"
              data-testid={`photo-card-${photo.id}`}
            >
              {photo.imageData && (
                <div className="aspect-square overflow-hidden">
                  <img
                    src={photo.imageData}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-parchemin/60 truncate">
                    {photo.joueurNom || "Anonyme"}
                  </span>
                  <Badge tone={tone(photo.statut)}>{photo.statut}</Badge>
                </div>
                {photo.caption && (
                  <p className="text-xs text-parchemin/75 line-clamp-2 italic font-display">
                    « {photo.caption} »
                  </p>
                )}
                <div className="flex gap-1.5 pt-2 border-t border-or/10">
                  {photo.statut !== "APPROUVEE" && (
                    <Btn
                      variant="secondary"
                      size="sm"
                      onClick={() => moderer(photo.id, "APPROUVEE", true)}
                      data-testid={`appr-${photo.id}`}
                    >
                      <Check className="w-3 h-3" />
                    </Btn>
                  )}
                  {photo.statut !== "REJETEE" && (
                    <Btn
                      variant="dangerText"
                      size="sm"
                      onClick={() => moderer(photo.id, "REJETEE", false)}
                      data-testid={`rej-${photo.id}`}
                    >
                      <X className="w-3 h-3" />
                    </Btn>
                  )}
                  <Btn
                    variant="ghost"
                    size="sm"
                    onClick={() => supprimer(photo.id)}
                    data-testid={`del-${photo.id}`}
                    className="ml-auto"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Btn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
